/**
 * ColorFilterDropdown - Color filter built on the generic FilterDropdown.
 *
 * Renders WUBRG + Multicolor/Colorless as a searchable multi-select dropdown
 * with mana-font icons.
 */
import type { ReactElement } from 'react';

import type { FilterDropdownOption } from '@/components/FilterDropdown/FilterDropdown';
import FilterDropdown from '@/components/FilterDropdown/FilterDropdown';
import type { Color } from '@/types/card';
import type { SpecialColor } from '@/types/schemas';

export interface ColorFilterDropdownProps {
  colors: Color[];
  specialColors?: SpecialColor[];
  onColorToggle: (color: Color) => void;
  onSpecialColorToggle?: (special: SpecialColor) => void;
  onClear: () => void;
  className?: string;
}

type ColorOptionValue = Color | SpecialColor;

const COLOR_ORDER: ColorOptionValue[] = ['W', 'U', 'B', 'R', 'G', 'multicolor', 'colorless'];

const COLOR_META: Record<ColorOptionValue, { label: string; iconClass: string; badgeClassName: string; isColorIdentity?: boolean }> = {
  W: { label: 'White', iconClass: 'ms-w', badgeClassName: 'bg-yellow-200/30 text-yellow-200' },
  U: { label: 'Blue', iconClass: 'ms-u', badgeClassName: 'bg-blue-400/20 text-blue-400' },
  B: { label: 'Black', iconClass: 'ms-b', badgeClassName: 'bg-zinc-400/20 text-zinc-300' },
  R: { label: 'Red', iconClass: 'ms-r', badgeClassName: 'bg-red-400/20 text-red-400' },
  G: { label: 'Green', iconClass: 'ms-g', badgeClassName: 'bg-green-400/20 text-green-400' },
  multicolor: { label: 'Multicolor', iconClass: 'ms-ci ms-ci-5', badgeClassName: 'bg-amber-400/20 text-amber-400', isColorIdentity: true },
  colorless: { label: 'Colorless', iconClass: 'ms-c', badgeClassName: 'bg-zinc-500/20 text-zinc-400' },
};

const ICON_STYLE = { fontSize: '16px' } as const;

const COLOR_OPTIONS: FilterDropdownOption[] = COLOR_ORDER.map((value) => {
  const meta = COLOR_META[value];
  return {
    value,
    label: meta.label,
    icon: <i className={`ms ${meta.iconClass}${meta.isColorIdentity ? '' : 'ms-cost'}`} style={ICON_STYLE} aria-hidden="true" />,
    badgeClassName: meta.badgeClassName,
  };
});

const TRIGGER_ICON = <i className="ms ms-ci ms-ci-5" style={{ fontSize: '14px' }} aria-hidden="true" />;

const COLORS: Color[] = ['W', 'U', 'B', 'R', 'G'];
const SPECIAL_COLORS: SpecialColor[] = ['multicolor', 'colorless'];

export default function ColorFilterDropdown({
  colors,
  specialColors = [],
  onColorToggle,
  onSpecialColorToggle,
  onClear,
  className,
}: ColorFilterDropdownProps): ReactElement {
  const selectedValues = [...colors, ...specialColors];

  const handleToggle = (value: string) => {
    if (COLORS.includes(value as Color)) {
      onColorToggle(value as Color);
    } else if (SPECIAL_COLORS.includes(value as SpecialColor) && onSpecialColorToggle) {
      onSpecialColorToggle(value as SpecialColor);
    }
  };

  return (
    <FilterDropdown
      triggerLabel="Colors"
      triggerIcon={TRIGGER_ICON}
      ariaLabel="Color filter"
      searchPlaceholder="Search colors..."
      options={COLOR_OPTIONS}
      selectedValues={selectedValues}
      onToggle={handleToggle}
      onClear={onClear}
      maxTriggerIcons={7}
      className={className}
    />
  );
}
